export const roundCurrency = (value) => Math.round((Number(value) || 0) * 100) / 100;

const TAX_APPLICABLE_COMPONENTS = [
  'COURSE_FEE',
  'REGISTRATION_FEE',
  'MEMBERSHIP_FEE',
  'INTERNATIONAL_PAYMENT_FEE',
  'MODULE_FEE',
  'PRODUCT_FEE',
  'INSTALLMENT_AMOUNT',
  'SURCHARGE',
  'OTHER_CHARGES',
  'GRADUATION_DEPOSIT_FEE',
  'GRADUATION_FEE',
  'GRADUATION_PASS_FEE',
  'GRADUATION_BALL_FEE',
];

const TAX_TW_APPLICABLE_COMPONENTS = [
  'TW_WORKSHOP_FEE',
  'TW_MATERIAL_FEE',
  'TW_CERTIFICATE_FEE',
  'TW_OTHER_CHARGES',
];

const TAX_EVENT_APPLICABLE_COMPONENTS = [
  'EVENT_ENTRY_FEE',
  'EVENT_SPONSORSHIP',
  'EVENT_TICKET',
];

const FEE_LIKE_INSTALLMENT_COMPONENTS = [
  'COURSE_FEE',
  'MODULE_FEE',
  'PRODUCT_FEE',
  'INSTALLMENT_AMOUNT',
];

const DEFAULT_DISCOUNT_COMPONENT_PRIORITY = [
  'COURSE_FEE',
  'MODULE_FEE',
  'PRODUCT_FEE',
  'INSTALLMENT_AMOUNT',
  'SURCHARGE',
  'OTHER_CHARGES',
  'TW_WORKSHOP_FEE',
  'EVENT_ENTRY_FEE',
  'EVENT_SPONSORSHIP',
  'EVENT_TICKET',
];

const sanitizeComponentAmounts = (components = {}) => {
  const normalized = {};
  Object.entries(components || {}).forEach(([key, value]) => {
    const amount = roundCurrency(value);
    if (amount > 0) {
      normalized[String(key).trim().toUpperCase()] = amount;
    }
  });
  return normalized;
};

const normalizeTaxConfig = (taxLike = {}, index = 0) => {
  const calculationMethod = String(taxLike.calculationMethod || 'INDEPENDENT').toUpperCase();
  const compoundingBase = String(taxLike.compoundingBase || 'BASE_ONLY').toUpperCase();
  const stageRaw = taxLike?.taxStage
    ? String(taxLike.taxStage).trim().toUpperCase()
    : (taxLike?.appliesAfterDiscount === false ? 'BEFORE_DISCOUNT' : 'AFTER_DISCOUNT');

  const applicableRaw = Array.isArray(taxLike.applicableComponents) ? taxLike.applicableComponents : [];
  const applicableComponents = [...new Set(
    applicableRaw
      .map((component) => String(component || '').trim().toUpperCase())
      .filter((component) => TAX_APPLICABLE_COMPONENTS.includes(component))
  )];

  const twApplicableRaw = Array.isArray(taxLike.twApplicableComponents) ? taxLike.twApplicableComponents : [];
  const twApplicableComponents = [...new Set(
    twApplicableRaw
      .map((component) => String(component || '').trim().toUpperCase())
      .filter((component) => TAX_TW_APPLICABLE_COMPONENTS.includes(component))
  )];

  const eventApplicableRaw = Array.isArray(taxLike.eventApplicableComponents)
    ? taxLike.eventApplicableComponents
    : [];
  const eventApplicableComponents = [...new Set(
    eventApplicableRaw
      .map((component) => String(component || '').trim().toUpperCase())
      .filter((component) => TAX_EVENT_APPLICABLE_COMPONENTS.includes(component))
  )];

  return {
    taxConfigId: taxLike.taxConfigId || taxLike._id || null,
    code: taxLike.code ? String(taxLike.code).trim().toUpperCase() : null,
    name: String(taxLike.name || taxLike.taxName || 'Tax').trim(),
    percentage: Number(taxLike.percentage) || 0,
    included: taxLike.included === true,
    isActive: taxLike.isActive !== false,
    allowForCrmCalculations: taxLike.allowForCrmCalculations !== false,
    allowForTwCalculations: taxLike.allowForTwCalculations === true,
    allowForEventCalculations: taxLike.allowForEventCalculations !== false,
    calculationMethod: calculationMethod === 'COMPOUND' ? 'COMPOUND' : 'INDEPENDENT',
    taxStage: stageRaw === 'BEFORE_DISCOUNT' ? 'BEFORE_DISCOUNT' : 'AFTER_DISCOUNT',
    compoundingBase: compoundingBase === 'BASE_PLUS_PREVIOUS_TAXES' ? 'BASE_PLUS_PREVIOUS_TAXES' : 'BASE_ONLY',
    applicableComponents: applicableComponents.length ? applicableComponents : [...TAX_APPLICABLE_COMPONENTS],
    twApplicableComponents,
    eventApplicableComponents,
    order: Number.isFinite(Number(taxLike.order)) ? Number(taxLike.order) : index,
  };
};

const applyDiscountToComponents = ({ components, discountAmount, discountPriority = DEFAULT_DISCOUNT_COMPONENT_PRIORITY }) => {
  const adjusted = { ...(components || {}) };
  let remainingDiscount = roundCurrency(discountAmount);
  if (remainingDiscount <= 0) {
    return { componentsAfterDiscount: adjusted, appliedDiscountAmount: 0 };
  }

  const keysByPriority = [...new Set([
    ...(Array.isArray(discountPriority) ? discountPriority.map((key) => String(key).trim().toUpperCase()) : []),
    ...Object.keys(adjusted),
  ])];

  keysByPriority.forEach((key) => {
    if (remainingDiscount <= 0) return;
    const currentAmount = roundCurrency(adjusted[key] || 0);
    if (currentAmount <= 0) return;
    const consumed = roundCurrency(Math.min(currentAmount, remainingDiscount));
    adjusted[key] = roundCurrency(currentAmount - consumed);
    remainingDiscount = roundCurrency(remainingDiscount - consumed);
  });

  return {
    componentsAfterDiscount: adjusted,
    appliedDiscountAmount: roundCurrency(Math.max(0, (Number(discountAmount) || 0) - remainingDiscount)),
  };
};

const sumComponentAmounts = (components, keys) => {
  if (!Array.isArray(keys) || keys.length === 0) return 0;
  return roundCurrency(keys.reduce((sum, key) => sum + (Number(components?.[key]) || 0), 0));
};

/**
 * Expand COURSE_FEE / MODULE_FEE / PRODUCT_FEE taxes so they also apply to INSTALLMENT_AMOUNT
 * when building module installment schedules.
 */
export const expandTaxesForInstallmentAmount = (taxes = []) => (
  (Array.isArray(taxes) ? taxes : []).map((tax, index) => {
    const comps = Array.isArray(tax?.applicableComponents)
      ? tax.applicableComponents.map((c) => String(c || '').trim().toUpperCase()).filter(Boolean)
      : [];

    if (!comps.length || comps.some((c) => FEE_LIKE_INSTALLMENT_COMPONENTS.includes(c))) {
      return {
        ...tax,
        order: Number.isFinite(Number(tax?.order)) ? Number(tax.order) : index,
        applicableComponents: [...new Set([...(comps.length ? comps : FEE_LIKE_INSTALLMENT_COMPONENTS), 'INSTALLMENT_AMOUNT'])],
      };
    }

    return {
      ...tax,
      order: Number.isFinite(Number(tax?.order)) ? Number(tax.order) : index,
      applicableComponents: comps,
    };
  })
);

/**
 * Mirrors services/crm-service + event-service taxCalculationEngine.calculateTaxBreakdown.
 * Supports CRM, Event, and T&W via crmOnly / eventOnly / twOnly flags.
 */
export const calculateTaxBreakdownForComponents = ({
  components,
  taxes,
  discountAmount = 0,
  currency = 'LKR',
  discountPriority = DEFAULT_DISCOUNT_COMPONENT_PRIORITY,
  crmOnly = true,
  eventOnly = false,
  twOnly = false,
}) => {
  const normalizedComponents = sanitizeComponentAmounts(components);
  const subtotalBeforeDiscount = roundCurrency(
    Object.values(normalizedComponents).reduce((sum, amount) => sum + (Number(amount) || 0), 0)
  );

  const { componentsAfterDiscount, appliedDiscountAmount } = applyDiscountToComponents({
    components: normalizedComponents,
    discountAmount,
    discountPriority,
  });
  const subtotalAfterDiscount = roundCurrency(
    Object.values(componentsAfterDiscount).reduce((sum, amount) => sum + (Number(amount) || 0), 0)
  );

  const normalizedTaxes = (Array.isArray(taxes) ? taxes : [])
    .map((tax, index) => normalizeTaxConfig(tax, index))
    .filter((tax) => {
      if (!tax.isActive || tax.percentage <= 0) return false;
      if (eventOnly) return tax.allowForEventCalculations;
      if (twOnly) return tax.allowForTwCalculations;
      if (crmOnly) return tax.allowForCrmCalculations;
      return true;
    })
    .sort((a, b) => (a.order !== b.order ? a.order - b.order : a.name.localeCompare(b.name)));

  const taxLines = [];
  ['BEFORE_DISCOUNT', 'AFTER_DISCOUNT'].forEach((stage) => {
    const stageTaxes = normalizedTaxes.filter((tax) => tax.taxStage === stage);
    const stageComponents = stage === 'BEFORE_DISCOUNT' ? normalizedComponents : componentsAfterDiscount;
    let previousTaxesForStage = 0;

    stageTaxes.forEach((tax) => {
      let componentList = tax.applicableComponents;
      if (eventOnly) {
        componentList = tax.eventApplicableComponents.length
          ? tax.eventApplicableComponents
          : TAX_EVENT_APPLICABLE_COMPONENTS;
      } else if (twOnly) {
        componentList = tax.twApplicableComponents.length
          ? tax.twApplicableComponents
          : TAX_TW_APPLICABLE_COMPONENTS;
      }

      const taxableComponents = componentList.filter((component) => Number(stageComponents?.[component]) > 0);
      const baseAmount = sumComponentAmounts(stageComponents, taxableComponents);
      if (baseAmount <= 0) return;

      const taxableBase = (
        tax.calculationMethod === 'COMPOUND' &&
        tax.compoundingBase === 'BASE_PLUS_PREVIOUS_TAXES'
      )
        ? roundCurrency(baseAmount + previousTaxesForStage)
        : baseAmount;

      const taxAmount = tax.included
        ? roundCurrency(taxableBase - (taxableBase / (1 + (tax.percentage / 100))))
        : roundCurrency(taxableBase * (tax.percentage / 100));
      if (taxAmount <= 0) return;

      previousTaxesForStage = roundCurrency(previousTaxesForStage + taxAmount);
      taxLines.push({
        taxConfigId: tax.taxConfigId,
        code: tax.code,
        name: tax.name,
        percentage: tax.percentage,
        included: tax.included,
        taxAmount,
        amount: taxAmount,
        currency,
        order: tax.order,
        stage,
        calculationMethod: tax.calculationMethod,
        compoundingBase: tax.compoundingBase,
        applicableComponents: taxableComponents,
        componentBaseAmount: baseAmount,
        taxableBaseAmount: taxableBase,
      });
    });
  });

  const inclusiveTaxAmount = roundCurrency(
    taxLines.filter((line) => line.included).reduce((sum, line) => sum + line.taxAmount, 0)
  );
  const exclusiveTaxAmount = roundCurrency(
    taxLines.filter((line) => !line.included).reduce((sum, line) => sum + line.taxAmount, 0)
  );

  return {
    currency,
    subtotalBeforeDiscount,
    discountAmount: appliedDiscountAmount,
    subtotalAfterDiscount,
    taxLines,
    inclusiveTaxAmount,
    exclusiveTaxAmount,
    totalTaxAmount: roundCurrency(inclusiveTaxAmount + exclusiveTaxAmount),
    totalInvoiceAmount: roundCurrency(subtotalAfterDiscount + exclusiveTaxAmount),
    finalNetAmount: roundCurrency(subtotalAfterDiscount + exclusiveTaxAmount),
    grandTotal: roundCurrency(subtotalAfterDiscount + exclusiveTaxAmount),
  };
};

/** Event entry fee / sponsorship preview using finance event tax configs. */
export const computeEventTaxBreakdown = ({
  amount = 0,
  componentKey = 'EVENT_ENTRY_FEE',
  taxes = [],
  discountAmount = 0,
  currency = 'LKR',
}) => {
  const component = String(componentKey || 'EVENT_ENTRY_FEE').trim().toUpperCase();
  return calculateTaxBreakdownForComponents({
    components: { [component]: roundCurrency(amount) },
    taxes,
    discountAmount,
    currency,
    crmOnly: false,
    eventOnly: true,
    twOnly: false,
    discountPriority: [component],
  });
};

/** T&W workshop fee preview using finance TW tax configs. */
export const computeTwTaxBreakdown = ({
  amount = 0,
  taxes = [],
  discountAmount = 0,
  currency = 'LKR',
  componentKey = 'TW_WORKSHOP_FEE',
}) => {
  const component = String(componentKey || 'TW_WORKSHOP_FEE').trim().toUpperCase();
  return calculateTaxBreakdownForComponents({
    components: { [component]: roundCurrency(amount) },
    taxes,
    discountAmount,
    currency,
    crmOnly: false,
    eventOnly: false,
    twOnly: true,
    discountPriority: [component],
  });
};

/**
 * Payment preview aligned with backend buildPaymentBreakdownSnapshot / taxCalculationEngine.
 */
export const computePaymentBreakdownPreview = ({
  courseAmount = 0,
  discountAmount = 0,
  membershipFeeAmount = 0,
  membershipFeeIncluded = false,
  registrationFeeAmount = 0,
  registrationFeeIncluded = false,
  internationalPaymentFeeAmount = 0,
  taxes = [],
  currency = 'LKR',
}) => {
  const breakdown = calculateTaxBreakdownForComponents({
    components: {
      COURSE_FEE: roundCurrency(courseAmount),
      REGISTRATION_FEE: registrationFeeIncluded ? 0 : roundCurrency(registrationFeeAmount),
      MEMBERSHIP_FEE: membershipFeeIncluded ? 0 : roundCurrency(membershipFeeAmount),
      INTERNATIONAL_PAYMENT_FEE: roundCurrency(internationalPaymentFeeAmount),
    },
    discountAmount: roundCurrency(discountAmount),
    taxes,
    currency,
    discountPriority: ['COURSE_FEE'],
  });

  const displayTaxes = breakdown.taxLines.map((tax) => ({
    name: tax.name,
    percentage: tax.percentage,
    included: tax.included,
    taxAmount: tax.taxAmount,
    currency: tax.currency || currency,
    order: tax.order,
    calculationMethod: tax.calculationMethod,
    compoundingBase: tax.compoundingBase,
  }));

  return {
    subtotalBeforeDiscount: breakdown.subtotalBeforeDiscount,
    subtotalAfterDiscount: breakdown.subtotalAfterDiscount,
    exclusiveTaxAmount: breakdown.exclusiveTaxAmount,
    inclusiveTaxAmount: breakdown.inclusiveTaxAmount,
    totalTaxAmount: breakdown.exclusiveTaxAmount,
    totalInvoiceAmount: breakdown.totalInvoiceAmount,
    taxes: displayTaxes,
    exclusiveTaxes: displayTaxes.filter((tax) => !tax.included),
    inclusiveTaxes: displayTaxes.filter((tax) => tax.included),
    grossTotal: breakdown.subtotalAfterDiscount,
  };
};

export const computeTaxesForGrossTotal = ({ taxes = [], grossTotal = 0, currency = 'LKR' }) => {
  const safeGrossTotal = roundCurrency(grossTotal);
  if (!Array.isArray(taxes) || taxes.length === 0 || safeGrossTotal <= 0) {
    return {
      taxes: [],
      exclusiveTaxes: [],
      inclusiveTaxes: [],
      exclusiveTaxAmount: 0,
      inclusiveTaxAmount: 0,
    };
  }

  // Use the shared engine on a single COURSE_FEE bucket so COMPOUND / order / stage match server.
  const taxesForEngine = taxes.map((tax) => ({
    ...tax,
    allowForCrmCalculations: tax.allowForCrmCalculations !== false,
    isActive: tax.isActive !== false,
    applicableComponents: ['COURSE_FEE'],
  }));

  const breakdown = calculateTaxBreakdownForComponents({
    components: { COURSE_FEE: safeGrossTotal },
    taxes: taxesForEngine,
    currency,
    discountAmount: 0,
    crmOnly: true,
  });

  const resolvedTaxes = breakdown.taxLines.map((tax) => ({
    name: tax.name,
    percentage: tax.percentage,
    included: tax.included,
    taxAmount: tax.taxAmount,
    currency: tax.currency || currency,
    order: tax.order,
    calculationMethod: tax.calculationMethod,
    compoundingBase: tax.compoundingBase,
  }));

  return {
    taxes: resolvedTaxes,
    exclusiveTaxes: resolvedTaxes.filter((tax) => !tax.included),
    inclusiveTaxes: resolvedTaxes.filter((tax) => tax.included),
    exclusiveTaxAmount: breakdown.exclusiveTaxAmount,
    inclusiveTaxAmount: breakdown.inclusiveTaxAmount,
  };
};

export const allocateTaxProportionallyAcrossInstallments = ({
  installments = [],
  totalTaxAmount = 0,
}) => {
  const map = new Map();
  const safeTotalTax = roundCurrency(totalTaxAmount);
  if (!Array.isArray(installments) || installments.length === 0 || safeTotalTax <= 0) {
    return map;
  }

  const baseAmounts = installments.map((inst) => (
    Number(inst?.netAmount) || Number(inst?.preDiscountAmount) || Number(inst?.amount) || 0
  ));
  const totalBase = baseAmounts.reduce((sum, amount) => sum + (Number(amount) || 0), 0);
  if (totalBase <= 0) return map;

  let assigned = 0;
  installments.forEach((inst, index) => {
    const installmentNumber = Number(inst?.installmentNumber) || index + 1;
    let allocated = 0;
    if (index === installments.length - 1) {
      allocated = roundCurrency(safeTotalTax - assigned);
    } else {
      allocated = roundCurrency((safeTotalTax * (baseAmounts[index] || 0)) / totalBase);
      assigned = roundCurrency(assigned + allocated);
    }
    map.set(installmentNumber, Math.max(0, allocated));
  });

  return map;
};

export const computeInstallmentTaxBreakdown = ({
  taxes = [],
  preDiscountAmount = 0,
  discountAmount = 0,
  currency = 'LKR',
}) => {
  const safePreDiscountAmount = roundCurrency(preDiscountAmount);
  const safeDiscountAmount = roundCurrency(Math.max(0, Math.min(discountAmount, safePreDiscountAmount)));

  const breakdown = calculateTaxBreakdownForComponents({
    components: { INSTALLMENT_AMOUNT: safePreDiscountAmount },
    taxes: expandTaxesForInstallmentAmount(taxes),
    discountAmount: safeDiscountAmount,
    currency,
    crmOnly: true,
    discountPriority: ['INSTALLMENT_AMOUNT'],
  });

  return {
    preDiscountAmount: safePreDiscountAmount,
    discountAmount: safeDiscountAmount,
    subtotalAfterDiscount: breakdown.subtotalAfterDiscount,
    taxLines: breakdown.taxLines.map((tax) => ({
      name: tax.name,
      percentage: tax.percentage,
      included: tax.included,
      taxAmount: tax.taxAmount,
      currency: tax.currency || currency,
      calculationMethod: tax.calculationMethod,
      compoundingBase: tax.compoundingBase,
      order: tax.order,
    })),
    exclusiveTaxTotal: breakdown.exclusiveTaxAmount,
    inclusiveTaxTotal: breakdown.inclusiveTaxAmount,
    amount: breakdown.totalInvoiceAmount,
  };
};
